/**
 * TanStack Query hooks for the dashboard home page.
 *
 * One hook per mini-section.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type {
  NominalRealResponse,
  NowcastResponse,
  TimeseriesPayload,
} from '@/features/dashboard/types';

/** Latest nowcast series for the mini sparkline. Defaults to US / 2y. */
export function useNowcastSummary(
  country: string = 'US',
  timeframe: string = '2y',
): UseQueryResult<NowcastResponse> {
  return useQuery<NowcastResponse>({
    queryKey: ['dashboard', 'nowcast', country, timeframe],
    queryFn: () =>
      apiGet<NowcastResponse>(endpoints.forecasts.nowcast({ country, timeframe })),
  });
}

/** MARS nominal/real/excess bar data for the Returns mini-section. */
export function useMarsNominalReal(
  timeframe: string = '3y',
): UseQueryResult<NominalRealResponse> {
  return useQuery<NominalRealResponse>({
    queryKey: ['dashboard', 'mars-nominal-real', timeframe],
    queryFn: () =>
      apiGet<NominalRealResponse>(
        endpoints.mars.returns({ view: 'nominal-real', timeframe }),
      ),
  });
}

/**
 * Server-side fetch window (days) — full history, independent of the display
 * range, mirroring the Central Bank timeseries page: the Kalman trend is
 * estimated on all available data, so this mini chart shows the same line as
 * the full page whatever window is displayed.
 */
const FULL_HISTORY_FETCH_DAYS = 365 * 30 + 90;

/** Fed monetary-policy guidance timeseries for the MP mini-section. */
export function useMonetaryPolicyTimeseries(
  period: string = '10y',
): UseQueryResult<TimeseriesPayload> {
  const fetchDays = FULL_HISTORY_FETCH_DAYS;
  return useQuery<TimeseriesPayload>({
    queryKey: ['dashboard', 'mp-timeseries', period],
    queryFn: () =>
      apiGet<TimeseriesPayload>(
        endpoints.centralBank.timeseries({
          topic: 'monetary_policy_guidance',
          period,
          fetchDays,
          version: 'v2',
          bank: 'Fed',
        }),
      ),
  });
}
